import type { BattleUnit, Base } from '../BattleUnit.ts';
import type { LeaderId } from '../constants.ts';
import { StateManager } from '../server/StateManager.ts';
import { randomSeed } from '../server/rng.ts';
import type { TerrainGrid } from '../server/terrain.ts';
import type {
  BattleEvent,
  SpellCommand,
  SwapRequest,
  TickCommands,
  ValidationResult,
} from '../server/types.ts';
import type { ServerAdapter } from './ServerAdapter.ts';

/**
 * LocalServerAdapter simulates a server-authoritative connection
 * by running a local instance of StateManager.
 * It is used for single-player testing without requiring a real network layer.
 *
 * Phase 3c Step 5: methods are declared async to match the new
 * ServerAdapter interface, but they all resolve synchronously — the
 * StateManager call happens before the Promise is returned, so there
 * is effectively zero latency introduced.
 *
 * Phase 3d Step 3: room methods are no-op stubs. Single-player just
 * pretends it's already in a 1-player "room" with myTeam=0; the
 * Game.ts MENU "Single Player" path calls these without expecting
 * real pairing. There are no event subscribers in single-player.
 */
export class LocalServerAdapter implements ServerAdapter {
  private manager!: StateManager;

  // -----------------------------------------------------------
  // Phase 3d — Room lifecycle (stubs for parity)
  // -----------------------------------------------------------

  async createRoom(): Promise<{ roomCode: string; myTeam: 0; terrainSeed: number }> {
    // Synthetic terrainSeed for single-mode parity. Game.ts doesn't
    // actually go through createRoom in single mode (it picks its own
    // seed in startSinglePlayer), but we honour the interface contract.
    return { roomCode: 'LOCAL0', myTeam: 0, terrainSeed: randomSeed() };
  }

  async joinRoom(_code: string): Promise<{ roomCode: string; myTeam: 1; terrainSeed: number }> {
    // Single-player has no real "join" path; this exists only to
    // satisfy the interface in case Game.ts calls it speculatively.
    return { roomCode: 'LOCAL0', myTeam: 1, terrainSeed: randomSeed() };
  }

  onEvent(_name: string, _handler: (data: any) => void): () => void {
    // Single-player never pushes events. Return an inert unsubscribe.
    return () => { /* noop */ };
  }

  // -----------------------------------------------------------
  // Battle session
  // -----------------------------------------------------------

  async initBattle(
    units: BattleUnit[],
    bases: Base[],
    seed: number,
    _boardSeed: number,
    terrainSeed: number,
    leaderByTeam?: [LeaderId, LeaderId],
  ): Promise<void> {
    // LocalServerAdapter doesn't re-run deployment / gem generation on its
    // own — the client has already consumed the seed to produce `units` and
    // the boards by the time we get here. We accept `seed` for interface
    // parity with NetworkServerAdapter (which echoes it to the server) and
    // stash it on StateManager for future server-authoritative gameplay.
    //
    // Phase 3e: `boardSeed` is ignored here. Single-player runs zero-latency
    // against a local fake server; there's no opponent to cheat against, so
    // no `Match3Mirror` is needed.
    //
    // Terrain: regenerate the same grid the client already drew during
    // DEPLOY so authoritative effect resolution agrees on positions.
    //
    // Session 9: stash per-team leaders so StateManager.validateSpellCommand
    // can gate spells against the leader's spellElements set.
    this.manager = new StateManager(units, bases);
    this.manager.state.seed = seed;
    this.manager.applyTerrainSeed(terrainSeed);
    this.manager.applyLeaders(leaderByTeam);
  }

  /**
   * Casual Mode hook — replace the seed-generated terrain with an
   * explicit grid AFTER `initBattle`. Used by Game.startCasualLevel to
   * inject the level designer's hand-authored terrain layout (see
   * `src/casual/CasualLevels.ts`).
   *
   * Client-only: NetworkServerAdapter has no equivalent because casual
   * mode runs purely against LocalServerAdapter. The server-side mirror
   * (`puzzle-tactician-server/`) doesn't need this method.
   *
   * Idempotent — does a shallow row-clone so subsequent EXEC mutation
   * (Fire ignites grass → burningGrass) doesn't leak back into the const
   * level data the caller passed in.
   */
  overrideTerrain(grid: TerrainGrid): void {
    if (!this.manager) return;
    this.manager.state.terrain = grid.map((row) => row.slice());
    // Mark seed as 0 so anything inspecting `terrainSeed` knows the grid
    // wasn't derived from a seed (no reproducible re-derivation path).
    this.manager.state.terrainSeed = 0;
  }

  /** @deprecated See `ServerAdapter.requestSwap` — kept for back-compat. */
  async requestSwap(req: SwapRequest): Promise<ValidationResult> {
    return this.manager.validateSwap(req);
  }

  async commitMatchResults(
    team: 0 | 1,
    stats: Record<string | number, number>,
    _options?: { swapLog?: SwapRequest[]; round?: number },
  ): Promise<void> {
    // Phase 3e: swapLog/round are ignored in single-player; LocalServerAdapter
    // is by definition trusted (the player only beats themselves).
    this.manager.commitMatchResults(team, stats);
  }

  async submitSchedule(
    team: 0 | 1,
    schedule: SpellCommand[],
  ): Promise<ValidationResult> {
    return this.manager.validateSchedule(team, schedule);
  }

  async executeExec(
    cmds: TickCommands,
  ): Promise<{ events: BattleEvent[]; winner: number }> {
    // In order for StateManager to allow executeExec, it needs to transition
    // from match3 -> sched -> exec.
    if (this.manager.state.phase === 'match3') {
      this.manager.enterScheduling();
    }
    // Execution will validate the schedule again inside.
    const result = this.manager.executeExec(cmds);
    // After EXEC finishes, if the game isn't over, StateManager doesn't automatically
    // transition back to match3, but in single-player, Game.ts resets its own phases.
    // For local simulation, we can just jump the phase back manually to simulate next round.
    if (result.winner === -1) {
      // simulate starting a new round
      this.manager.state.phase = 'match3';
      this.manager.state.playerStats = {};
      this.manager.state.aiStats = {};
      this.manager.state.playerUsedEls = [];
      this.manager.state.aiUsedEls = [];
    }
    return result;
  }
}
